/**
 * MODULE: core/tenant/frontend/hooks/useTenants.ts
 * PURPOSE: Tenant management hooks - React Query hooks for tenant CRUD operations
 *
 * DEPENDENCIES:
 *   - @tanstack/react-query: useQuery, useMutation, useQueryClient for data fetching
 *   - ../api/tenantApi: tenantApi for API calls
 *   - ../../contracts/tenant.types: Tenant, CreateTenantRequest, UpdateTenantRequest types
 *
 * PUBLIC API:
 *   - useTenants(): Query hook for listing tenants
 *   - useTenantById(id): Query hook for getting single tenant
 *   - useCreateTenant(): Mutation hook for creating tenant
 *   - useUpdateTenant(): Mutation hook for updating tenant
 *   - useDeleteTenant(): Mutation hook for deleting tenant
 *
 * SIDE EFFECTS:
 *   - Makes API calls via tenantApi
 *   - Manages React Query cache (invalidates queries on mutations)
 *   - Updates query cache on successful mutations
 *
 * AGENT SAFETY: SAFE
 * FILE SIZE: 60 / 100 lines
 *
 * CHANGE LOG:
 *   - 2026-01-20: Created - Tenant management hooks with React Query
 *     Provides hooks for tenant CRUD operations
 *     Handles query caching and invalidation
 *     Related: tenantApi, TenantProvider
 *
 * See: /README.md#documentation | /ARCHITECTURE.md | /core/tenant/README.md
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantApi } from '../api/tenantApi';
import type {
  Tenant,
  CreateTenantRequest,
  UpdateTenantRequest,
} from '../../contracts/tenant.types';

const TENANTS_KEY = ['tenants'];

export function useTenants() {
  return useQuery({
    queryKey: TENANTS_KEY,
    queryFn: tenantApi.list,
  });
}

export function useTenantById(id: string) {
  return useQuery({
    queryKey: [...TENANTS_KEY, id],
    queryFn: () => tenantApi.get(id),
    enabled: !!id,
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTenantRequest) => tenantApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANTS_KEY });
    },
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTenantRequest }) =>
      tenantApi.update(id, data),
    onSuccess: (updatedTenant) => {
      queryClient.invalidateQueries({ queryKey: TENANTS_KEY });
      queryClient.setQueryData([...TENANTS_KEY, updatedTenant.id], updatedTenant);
    },
  });
}

export function useDeleteTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tenantApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANTS_KEY });
    },
  });
}
